import { gql } from '@apollo/client';
import { Input, Textarea } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { isEqual } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import {
  type NewsroomDataCompanyDescription,
  useNewsroomSetup,
} from '../../../contexts/NewsroomSetupContext';
import { useUpdateCompanyDescriptionOnboardingMutation } from '../../../generated/hooks';
import { track } from '../../../lib/tracker';
import Footer from './Footer';
import Header from './Header';
import { Form, StepForm, StepSectionWrapper } from './styles';

gql`
  fragment CompanyDescription on NewsroomSettings {
    id
    tagline
    companyDescription
  }

  mutation UpdateCompanyDescriptionOnboarding(
    $newsroomId: ID!
    $tagline: String
    $companyDescription: String
  ) {
    newsroomSettingsUpdate(
      input: { newsroomId: $newsroomId, tagline: $tagline, companyDescription: $companyDescription }
    ) {
      error {
        ... on NewsroomSettingsUpdateValidationError {
          companyDescription
          tagline
        }
      }
      newsroomSettings {
        id
        ...CompanyDescription
      }
    }
  }
`;

const CompanyDescription = () => {
  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const { pressroomId } = useCurrentPressroom();
  const { newsroomData, handleNewsroomDataFieldChange, setNewsroomSetupStep } = useNewsroomSetup();

  const formRef = useRef<HTMLFormElement>(null);
  const initialValues = useRef<NewsroomDataCompanyDescription>(newsroomData['company-description']);

  const [updateSettings, { loading }] = useUpdateCompanyDescriptionOnboardingMutation({
    onCompleted: () => {
      const dataChanged = !isEqual(initialValues.current, newsroomData['company-description']);
      track({
        event: 'Newsroom Setup: Company Description - Proceed',
        traits: {
          step: 3,
          dataChanged,
        },
      });
      setNewsroomSetupStep('company-info');
    },
    onError: () => showFailureToast(t('common_general_error')),
    variables: {
      newsroomId: pressroomId,
      tagline: newsroomData['company-description'].tagline,
      companyDescription: newsroomData['company-description'].companyDescription,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: ?
  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
    const prepopulated = Boolean(
      newsroomData['company-description'].tagline &&
        newsroomData['company-description'].companyDescription,
    );
    track({ event: 'Newsroom Setup: Company Description', traits: { step: 3, prepopulated } });
  }, []);

  return (
    <StepForm
      ref={formRef}
      onSubmit={e => {
        e.preventDefault();
        updateSettings();
      }}
    >
      <StepSectionWrapper>
        <Header currentStep={3} heading={t('onboarding-company-description_title')} />
        <Form>
          <Input
            label={t('newsroom_settings-general_settings-slogan-input_label')}
            value={newsroomData['company-description'].tagline}
            onChange={e =>
              handleNewsroomDataFieldChange('company-description', 'tagline', e.target.value)
            }
          />
          <Textarea
            label={t('common_description')}
            rows={6}
            helperText={t('onboarding-company-description-placeholder')}
            value={newsroomData['company-description'].companyDescription}
            onChange={e =>
              handleNewsroomDataFieldChange(
                'company-description',
                'companyDescription',
                e.target.value,
              )
            }
          />
        </Form>
        <Footer
          isSaving={loading}
          canContinue
          previousStep="press-contact"
          nextStep="company-info"
        />
      </StepSectionWrapper>
    </StepForm>
  );
};

export default CompanyDescription;
